/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Inject, Injectable, InjectionToken } from '@angular/core';
import { MessageBasket } from '@gh/core-messages';
import { EMPTY$, of$, throwError$ } from '@gh/rx';
import { filter$, first$, mapTo$ } from '@gh/rx/operators';
import { BehaviorSubject, Observable } from 'rxjs';
import { GoogleApiLoader } from './google-api-loader.service';

enum GoogleAuthStatus { Unknown, Initializing, Initialized, Error}

export interface ExternalGoogleConfig extends google.accounts.oauth2.TokenClientConfig {}
export interface GoogleTokenResponse extends google.accounts.oauth2.TokenResponse {}
export interface GoogleTokenClient extends google.accounts.oauth2.TokenClient {}

export const GOOGLE_CONFIG = new InjectionToken<ExternalGoogleConfig>('ExternalGoogleConfig');

const handleErrors = ({ error }: any) => {
  if (error === 'popup_closed_by_user') {
    return EMPTY$;
  } else {
    return throwError$(MessageBasket.error(`msg_google_auth:${error}`));
  }
};

@Injectable()
export class GoogleAuthService {
  private status$$ = new BehaviorSubject(GoogleAuthStatus.Unknown);
  private tokenClient: GoogleTokenClient;
  private pickerInited: boolean;
  constructor(private loader: GoogleApiLoader,
              @Inject(GOOGLE_CONFIG) private googleConfig: ExternalGoogleConfig) {
    if (!this.tokenClient && !this.getTokenFromStorage()) {
      this.startInit();
    } else {
      this.status$$.next(GoogleAuthStatus.Initialized);
    }
  }

  get accessToken(): string {
    return this.getTokenFromStorage();
  }

  onPickerLibLoaded(): void {
    this.pickerInited = true;
  }

  signIn(callBack?: Function): void {
    if (!!this.getTokenFromStorage()) {
      if (callBack) {
        callBack();
      }
      return;
    }
    if (!this.tokenClient) {
      this.status$$.next(GoogleAuthStatus.Unknown);
      this.startInit();
      this.status$$.subscribe((status) => {
        if (status === GoogleAuthStatus.Initialized) {
          this.signIn(callBack);
        }
      });
    }
    if (callBack) {
      // @ts-ignore
      this.tokenClient.callback = (res: any) => {
        this.callback(res);
        callBack(res);
      };
    }
    if (!this.accessToken) {
      // Prompt the user to select a Google Account
      // when establishing a new session.
      this.tokenClient.requestAccessToken({prompt: ''});
    } else {
      if (callBack) {
        callBack();
      }
      // Skip display of account chooser and consent dialog for an existing session.
      // this.tokenClient.requestAccessToken({prompt: ''});
    }
  }

  signOut(): void {
    if (this.status$$.value === GoogleAuthStatus.Unknown) {
      return;
    }
  }

  init(): Observable<boolean> {
    switch (this.status$$.value) {
      case GoogleAuthStatus.Initialized:
        return of$(true);
      case GoogleAuthStatus.Initializing:
        return this.status$$.pipe(filter$((status) => status === GoogleAuthStatus.Initialized), mapTo$(true), first$());
      default:
        this.signIn();
        return this.status$$.pipe(filter$((status) => status === GoogleAuthStatus.Initialized), mapTo$(true), first$());
    }
  }

  grantOfflineAccess(): Observable<boolean> {
    this.signIn();
    return this.init();
  }

  private callback(response: GoogleTokenResponse): void {
    const { status$$ } = this;
    if (response.error !== undefined) {
      status$$.next(GoogleAuthStatus.Error);
      throw (response);
    }
    sessionStorage.setItem('googleAuth', JSON.stringify({
                                                          accessToken: response.access_token,
                                                          // tslint:disable-next-line:no-magic-numbers
                                                          expireDate: new Date().getTime() + parseInt(response.expires_in) * 1000,
    }));
    status$$.next(GoogleAuthStatus.Initialized);
  }

  private startInit(): void {
    const { status$$ } = this;
    const config = {
      ...this.googleConfig,
      callback: (res) => {
        this.callback(res);
      } } as ExternalGoogleConfig;
    if (!window.google || !window.google.accounts) {
      // tslint:disable-next-line:no-magic-numbers
      setTimeout(() => this.startInit(), 1000);
    } else {
      this.tokenClient = window.google.accounts.oauth2.initTokenClient(config);
      status$$.next(GoogleAuthStatus.Initializing);
    }
    // client.requestAccessToken();

    // this.loader.load('auth2').subscribe(
    //   () => {
    //     const auth = gapi.auth2.init(this.googleConfig);
    //
    //     auth.then(
    //       () => status$$.next(GoogleAuthStatus.Initialized),
    //       () => status$$.next(GoogleAuthStatus.Error));
    //   },
    //   () => status$$.next(GoogleAuthStatus.Error));
  }

  private getTokenFromStorage(): string {
    const googleAuth = sessionStorage.getItem('googleAuth');
    if (!!googleAuth) {
      const tokenJson = JSON.parse(googleAuth);
      const accessToken = tokenJson.accessToken;
      const expire = tokenJson.expireDate;
      return accessToken && expire > new Date().getTime() ? accessToken : '';
    }
    return '';
  }
}
